import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import CalendarClockIcon from '@/assets/icons/calendar-clock.svg?react';
import loginSupportIllustration from '@/assets/illustrations/main.png';
import './Home.scss';
import { Header } from '@/components/Header';
import { PrivacyPolicyButton } from '@/components/PrivacyPolicyButton';

export function Home() {
    const navigate = useNavigate();
    const { login, isLoggedIn, isLoading: authLoading } = useAuth();
    const [studentNumber, setStudentNumber] = useState('');
    const [password, setPassword] = useState('');
    const [currentTime, setCurrentTime] = useState(new Date());
    const [error, setError] = useState('');
    const [isLoggingIn, setIsLoggingIn] = useState(false);

    // 이미 로그인되어 있으면 Selection 페이지로
    useEffect(() => {
        if (!authLoading && isLoggedIn) {
            navigate('/selection');
        }
    }, [authLoading, isLoggedIn, navigate]);

    // 현재 시간 업데이트
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    // 날짜/시간 포맷
    const formatDateTime = (date: Date) => {
        const month = date.getMonth() + 1;
        const day = date.getDate();
        const dayOfWeek = ['일', '월', '화', '수', '목', '금', '토'][date.getDay()];
        const hours = date.getHours();
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const ampm = hours < 12 ? '오전' : '오후';
        const hour12 = hours % 12 || 12;

        return `${month}월 ${day}일 (${dayOfWeek}) ${ampm} ${hour12}:${minutes}`;
    };

    // 로그인
    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!studentNumber || !password) {
            setError('학번과 비밀번호를 모두 입력해주세요.');
            return;
        }

        setIsLoggingIn(true);
        const result = await login(Number(studentNumber), password);

        if (result.success) {
            navigate('/selection');
        } else {
            setError(result.message || '학번 또는 비밀번호가 올바르지 않습니다.');
        }

        setIsLoggingIn(false);
    };


    return (
        <div className="home-page">
            {/* 헤더 */}
            <Header />

            {/* 메인 영역 */}
            <main className="home-main">
                {/* 타이틀 */}
                <div className="home-title">
                    <CalendarClockIcon className="home-title__icon" />
                    <div className="home-title__text">
                        <h1>공강 스케줄 통합 예약</h1>
                        <p>스터디카페 좌석 예약, 별빛버스 승차 예약</p>
                    </div>
                </div>

                {/* 컨텐츠 영역 */}
                <div className="home-content">
                    {/* 로그인 폼 */}
                    <form className="login-form" onSubmit={handleLogin}>
                        <div className="form-group">
                            <label className="form-label">
                                학번 <span className="check-icon">✓</span>
                            </label>
                            <input
                                type="text"
                                className="form-input"
                                value={studentNumber}
                                onChange={(e) => {
                                    setStudentNumber(e.target.value);
                                    setError('');
                                }}
                                inputMode="numeric"
                                pattern="[0-9]*"
                                autoComplete="username"
                                placeholder=""
                                disabled={isLoggingIn}
                            />
                        </div>

                        <div className="form-group">
                            <label className="form-label">
                                비밀번호 <span className="check-icon">✓</span>
                            </label>
                            <input
                                type="password"
                                className="form-input"
                                value={password}
                                onChange={(e) => {
                                    setPassword(e.target.value);
                                    setError('');
                                }}
                                autoComplete="current-password"
                                placeholder=""
                                disabled={isLoggingIn}
                            />
                        </div>

                        {error && (
                            <div className="login-error">
                                {error}
                            </div>
                        )}

                        <a
                            href="/forgot-password"
                            className="forgot-password"
                            onClick={(e) => {
                                e.preventDefault();
                                navigate('/forgot-password');
                            }}
                        >
                            비밀번호를 잊으셨나요?
                        </a>

                        <button type="submit" className="login-btn" disabled={isLoggingIn}>
                            {isLoggingIn ? '로그인 중...' : '로그인'}
                        </button>

                        <div className="login-form__privacy-policy">
                            <PrivacyPolicyButton className="privacy-policy-inline-btn" />
                        </div>
                    </form>

                    <div className="home-illustration" aria-hidden="true">
                        <img src={loginSupportIllustration} alt="" />
                    </div>
                </div>
            </main>
        </div>
    );
}
